/**
 * sirus/LfgPanel — R232q21 Phase 4.
 *
 * Sirus 3.3.5a-style "Поиск группы" finder. Toggled via the `I` micro-button
 * (panel flag `lfg`). Four tabs:
 *   – Подземелья (heroic + normal 5-mans)
 *   – Рейды (10/25-man)
 *   – Арена (2v2 / 3v3 / 5v5)
 *   – Поля боя (regular battlegrounds)
 *
 * Each row = one activity with role pickers (tank/heal/dps placeholder
 * checkboxes), a «Записаться» button and an optional queue indicator. Queue
 * state is local-only so the user can see the UX flow without backend.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useUiPanels } from './MicroMenu.js';

/* ─────────────────────────────────────────────────────────────────
 * Mock data
 * ──────────────────────────────────────────────────────────────── */

type TabId = 'dungeons' | 'raids' | 'arena' | 'bg';

const TABS: { id: TabId; label: string }[] = [
  { id: 'dungeons', label: 'Подземелья' },
  { id: 'raids',    label: 'Рейды' },
  { id: 'arena',    label: 'Арена' },
  { id: 'bg',       label: 'Поля боя' },
];

interface Activity {
  id: string;
  name: string;
  meta: string;
  minLevel: number;
  category: TabId;
}

const ACTIVITIES: Activity[] = [
  // Dungeons
  { id: 'd1',  name: 'Утопленные катакомбы',     meta: 'Норм. · 5 чел.',    minLevel: 65, category: 'dungeons' },
  { id: 'd2',  name: 'Цитадель Ледяной Короны',  meta: 'Героик · 5 чел.',   minLevel: 80, category: 'dungeons' },
  { id: 'd3',  name: 'Рассветный Двор',          meta: 'Героик · 5 чел.',   minLevel: 80, category: 'dungeons' },
  { id: 'd4',  name: 'Зал Камней',               meta: 'Норм. · 5 чел.',    minLevel: 75, category: 'dungeons' },

  // Raids
  { id: 'r1',  name: 'Ульдуар',                  meta: '10 человек',        minLevel: 80, category: 'raids' },
  { id: 'r2',  name: 'Ульдуар',                  meta: '25 человек',        minLevel: 80, category: 'raids' },
  { id: 'r3',  name: 'Цитадель Ледяной Короны',  meta: '10 человек',        minLevel: 80, category: 'raids' },
  { id: 'r4',  name: 'Цитадель Ледяной Короны',  meta: '25 человек · героик', minLevel: 80, category: 'raids' },

  // Arena
  { id: 'a1',  name: 'Арена 2v2',                meta: 'Рейтинговая',       minLevel: 80, category: 'arena' },
  { id: 'a2',  name: 'Арена 3v3',                meta: 'Рейтинговая',       minLevel: 80, category: 'arena' },
  { id: 'a3',  name: 'Арена 5v5',                meta: 'Рейтинговая',       minLevel: 80, category: 'arena' },

  // BG
  { id: 'b1',  name: 'Ущелье Песни Войны',       meta: '10v10',             minLevel: 70, category: 'bg' },
  { id: 'b2',  name: 'Низина Арати',             meta: '15v15',             minLevel: 60, category: 'bg' },
  { id: 'b3',  name: 'Альтеракская долина',      meta: '40v40',             minLevel: 60, category: 'bg' },
];

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

interface RoleFlags {
  tank: boolean;
  heal: boolean;
  dps: boolean;
}

interface QueueState {
  position: number;
  eta: string;
}

export function LfgPanel() {
  const close = () => useUiPanels.getState().set('lfg', false);
  const [tab, setTab] = useState<TabId>('dungeons');
  const [roles, setRoles] = useState<RoleFlags>({ tank: false, heal: false, dps: true });
  const [queues, setQueues] = useState<Record<string, QueueState>>({});

  /* ── Esc closes ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const enqueue = (id: string) => {
    setQueues((prev) => ({
      ...prev,
      [id]: prev[id]
        ? prev[id]
        : { position: Math.floor(Math.random() * 18) + 2, eta: `${Math.floor(Math.random() * 14) + 1} мин` },
    }));
  };

  const dequeue = (id: string) => {
    setQueues((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const filtered = ACTIVITIES.filter((a) => a.category === tab);

  return (
    <div role="dialog" aria-modal style={overlayStyle} onClick={close}>
      <div onClick={(e) => e.stopPropagation()}>
        <SirusFrame size="md" style={{ width: 580, height: 540, position: 'relative' }}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Поиск группы</span>
            <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
          </div>

          {/* Role pickers */}
          <div style={{ display: 'flex', gap: 12, padding: '8px 0', alignItems: 'center' }}>
            <span style={subtitleStyle}>Ваши роли:</span>
            <RoleCheckbox label="Танк" checked={roles.tank} onChange={(v) => setRoles({ ...roles, tank: v })} />
            <RoleCheckbox label="Лекарь" checked={roles.heal} onChange={(v) => setRoles({ ...roles, heal: v })} />
            <RoleCheckbox label="Бой" checked={roles.dps} onChange={(v) => setRoles({ ...roles, dps: v })} />
          </div>

          {/* Tabs */}
          <div style={tabRowStyle}>
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    ...tabBtnStyle,
                    background: active ? 'rgba(201,169,106,0.15)' : 'transparent',
                    color: active ? '#FFD479' : 'var(--aiwari-fg-muted, #B19B7E)',
                    borderBottomColor: active ? 'var(--aiwari-gold, #FAB300)' : 'transparent',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* List */}
          <div style={listWrapStyle}>
            {filtered.map((a) => {
              const q = queues[a.id];
              return (
                <div key={a.id} style={rowStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={nameStyle}>{a.name}</div>
                    <div style={subStyle}>{a.meta} · от {a.minLevel} ур.</div>
                  </div>
                  {q && (
                    <span style={queuePillStyle}>
                      № {q.position} · ~{q.eta}
                    </span>
                  )}
                  {q ? (
                    <button type="button" style={dangerBtnStyle} onClick={() => dequeue(a.id)}>Покинуть</button>
                  ) : (
                    <button type="button" style={primaryBtnStyle} onClick={() => enqueue(a.id)}>Записаться</button>
                  )}
                </div>
              );
            })}
          </div>
        </SirusFrame>
      </div>
    </div>
  );
}

function RoleCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--aiwari-gold, #C9A96A)' }}
      />
      <span style={{ color: checked ? '#FFD479' : 'var(--aiwari-fg-muted, #B19B7E)' }}>{label}</span>
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 8,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 18,
  letterSpacing: 0.8,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const subtitleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 12,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  letterSpacing: 0.4,
};

const closeBtnStyle: CSSProperties = {
  width: 28, height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18, lineHeight: 1, cursor: 'pointer',
  fontFamily: 'inherit',
};

const tabRowStyle: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const tabBtnStyle: CSSProperties = {
  padding: '8px 16px',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.4,
  cursor: 'pointer',
};

const listWrapStyle: CSSProperties = {
  height: 380,
  marginTop: 10,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

const nameStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 14,
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const subStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontStyle: 'italic',
};

const queuePillStyle: CSSProperties = {
  padding: '3px 10px',
  background: 'rgba(11,9,7,0.7)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  borderRadius: 999,
  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
  fontSize: 11,
  color: '#FFD479',
};

const primaryBtnStyle: CSSProperties = {
  padding: '5px 14px',
  background: 'linear-gradient(180deg, #2A2014, #1F1813)',
  border: '1px solid var(--aiwari-gold-dark, #7A5A18)',
  color: 'var(--aiwari-gold-soft, #FFD479)',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 12,
  cursor: 'pointer',
};

const dangerBtnStyle: CSSProperties = {
  padding: '5px 14px',
  background: 'transparent',
  border: '1px solid #5C2520',
  color: '#FF8A80',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 12,
  cursor: 'pointer',
};

export default LfgPanel;
